<template>
    <div class="zesa-portal">
        <!-- Hero Section -->
        <section class="portal-hero">
            <div class="hero-overlay"></div>
            <div class="container hero-inner text-center">
                <img style="height: 48px" src="/img/zetdc-logo.webp" alt="ZETDC Logo" class="mb-3 hero-logo">
                <h1 class="hero-title">ZESA Self-Service Portal</h1>
                <p class="hero-subtitle">
                    Buy tokens, view your purchase history, check tariffs, and manage your electricity — all in one place.
                </p>
                <div class="hero-actions">
                    <router-link to="/zesa" class="btn btn-success btn-lg hero-btn">
                        <i class="fas fa-bolt mr-2"></i>Buy ZESA Now
                    </router-link>
                    <router-link to="/zesa/view-token" class="btn btn-outline-light btn-lg hero-btn">
                        <i class="fas fa-search mr-2"></i>View My Tokens
                    </router-link>
                </div>
            </div>
        </section>

        <!-- Quick Actions Grid -->
        <section class="py-5 bg-white">
            <div class="container">
                <h2 class="section-title text-center mb-2">What would you like to do?</h2>
                <p class="text-center text-muted mb-4" style="font-size: 0.95rem;">Your ZESA self-service dashboard — everything you need in one place.</p>
                <div class="row action-grid">
                    <div class="col-6 col-md-4 mb-3" v-for="action in actions" :key="action.title">
                        <component :is="action.external ? 'a' : 'router-link'"
                                   :to="!action.external ? action.link : undefined"
                                   :href="action.external ? action.link : undefined"
                                   :target="action.external ? '_blank' : undefined"
                                   class="action-card d-block text-decoration-none h-100">
                            <div class="action-icon-wrap" :style="{ background: action.gradient }">
                                <i :class="action.icon" class="action-icon"></i>
                            </div>
                            <h3 class="action-title">{{ action.title }}</h3>
                            <p class="action-desc">{{ action.desc }}</p>
                        </component>
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Use Magetsi -->
        <section class="py-5 bg-light-section">
            <div class="container">
                <h2 class="section-title text-center mb-4">Why Use Magetsi as Your ZESA Portal?</h2>
                <div class="row">
                    <div class="col-md-4 mb-4" v-for="feature in features" :key="feature.title">
                        <div class="feature-card text-center">
                            <div class="feature-icon-wrap mx-auto mb-3">
                                <i :class="feature.icon" class="feature-icon"></i>
                            </div>
                            <h3 class="feature-title">{{ feature.title }}</h3>
                            <p class="feature-desc">{{ feature.desc }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works -->
        <section class="py-5 bg-white">
            <div class="container">
                <h2 class="section-title text-center mb-4">How It Works</h2>
                <div class="steps-row">
                    <div class="step-item" v-for="(step, i) in steps" :key="i">
                        <div class="step-number">{{ i + 1 }}</div>
                        <div class="step-connector" v-if="i < steps.length - 1"></div>
                        <h3 class="step-title">{{ step.title }}</h3>
                        <p class="step-desc">{{ step.desc }}</p>
                    </div>
                </div>
                <div class="text-center mt-4">
                    <router-link to="/zesa" class="btn btn-success shadow-none px-4">
                        Get Started <i class="fas fa-arrow-right ml-2"></i>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- SEO Content -->
        <section class="py-5 bg-light-section">
            <div class="container">
                <div class="seo-content mx-auto">
                    <h2 class="section-title mb-3">What Is the ZESA Self-Service Portal?</h2>
                    <p>
                        The ZESA self-service portal on Magetsi gives you instant access to all the electricity management tools you need.
                        Instead of queuing at ZETDC offices or navigating the official ZESA portal, you can buy prepaid tokens, view your
                        complete purchase history, check the latest ZESA tariffs, and calculate exactly how many units your money will buy — all
                        from your phone or computer, 24 hours a day, 7 days a week.
                    </p>
                    <p>
                        Whether you're looking for the ZETDC self-service portal to check your token or simply want to buy ZESA online quickly,
                        Magetsi is the most convenient way to manage your electricity in Zimbabwe. Our platform supports payments via EcoCash,
                        Visa, and Mastercard, with instant token delivery via SMS and WhatsApp.
                    </p>

                    <h2 class="section-title mt-5 mb-3">ZESA Portal Services Available on Magetsi</h2>
                    <p>
                        Magetsi brings together all the ZESA self-service tools you need under one roof. Here's what you can do on our online ZESA portal:
                    </p>
                    <ul>
                        <li><strong>Buy ZESA tokens online</strong> — enter your meter number, choose your amount, pay via EcoCash or card, and receive your token instantly.</li>
                        <li><strong>View your ZESA token history</strong> — enter your meter number to retrieve every token you've purchased through Magetsi. No login required.</li>
                        <li><strong>Check current ZESA tariffs</strong> — see the latest electricity tariff bands so you know exactly what you'll pay per unit.</li>
                        <li><strong>ZESA calculator</strong> — find out how many kilowatt-hours your budget will get you before you buy.</li>
                        <li><strong>Maximise your ZESA savings</strong> — learn how ZESA's stepped tariff system works and how to time your purchases for maximum value.</li>
                        <li><strong>Buy via WhatsApp</strong> — chat with our Magetsi bot to purchase ZESA tokens without even opening a browser.</li>
                    </ul>

                    <h2 class="section-title mt-5 mb-3">How to Access ZESA Self-Service Online</h2>
                    <p>
                        Accessing ZESA self-service through Magetsi is straightforward. You don't need to create an account or remember
                        any passwords. Simply visit <router-link to="/">magetsi.co.zw</router-link>, navigate to the ZESA section, and
                        enter your prepaid meter number. Within seconds, you can purchase electricity, view past tokens, or check the current tariff rates.
                    </p>
                    <p>
                        For those who prefer messaging, our WhatsApp chatbot at
                        <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank">+263 78 583 2033</a>
                        offers the same ZESA self-service experience directly in your chat — buy tokens, check your balance, and get
                        instant support without downloading any app.
                    </p>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="py-5 bg-white">
            <div class="container">
                <div class="faq-section mx-auto">
                    <h2 class="section-title text-center mb-4">Frequently Asked Questions</h2>
                    <div class="faq-list">
                        <div class="faq-item" v-for="(faq, i) in faqs" :key="i">
                            <div class="faq-question" @click="toggleFaq(i)">
                                <h3 class="faq-q-text">{{ faq.q }}</h3>
                                <i :class="openFaq === i ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="faq-chevron"></i>
                            </div>
                            <transition name="faq-slide">
                                <div v-if="openFaq === i" class="faq-answer">
                                    <p v-html="faq.a"></p>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="portal-cta">
            <div class="container text-center">
                <h2 class="cta-title">Ready to Manage Your Electricity?</h2>
                <p class="cta-subtitle">Join thousands of Zimbabweans who use Magetsi as their ZESA self-service portal.</p>
                <div class="cta-actions">
                    <router-link to="/zesa" class="btn btn-success btn-lg hero-btn">
                        <i class="fas fa-bolt mr-2"></i>Buy ZESA
                    </router-link>
                    <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank" class="btn btn-whatsapp btn-lg hero-btn">
                        <i class="fab fa-whatsapp mr-2"></i>Chat on WhatsApp
                    </a>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
export default {
    name: "ZesaPortal",
    metaInfo() {
        return {
            title: 'ZESA Self-Service Portal',
        };
    },
    data() {
        return {
            openFaq: null,
            actions: [
                {
                    title: 'Buy ZESA Tokens',
                    desc: 'Purchase prepaid electricity instantly',
                    icon: 'fas fa-bolt',
                    gradient: 'linear-gradient(135deg, #28a745, #20c997)',
                    link: '/zesa',
                },
                {
                    title: 'View My Tokens',
                    desc: 'Retrieve your token purchase history',
                    icon: 'fas fa-search',
                    gradient: 'linear-gradient(135deg, #1a56db, #4a7cfb)',
                    link: '/zesa/view-token',
                },
                {
                    title: 'ZESA Calculator',
                    desc: 'See how many units your money buys',
                    icon: 'fas fa-calculator',
                    gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    link: '/calculators/zesa-calculator',
                },
                {
                    title: 'Current Tariffs',
                    desc: 'View the latest electricity tariff bands',
                    icon: 'fas fa-chart-bar',
                    gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    link: '/zesa-tariffs',
                },
                {
                    title: 'Save on Electricity',
                    desc: 'Maximise your ZESA savings',
                    icon: 'fas fa-lightbulb',
                    gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                    link: '/maximise-your-zesa-savings',
                },
                {
                    title: 'Buy on WhatsApp',
                    desc: 'Purchase ZESA via our chatbot',
                    icon: 'fab fa-whatsapp',
                    gradient: 'linear-gradient(135deg, #25d366, #128c7e)',
                    link: 'https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi',
                    external: true,
                },
            ],
            features: [
                {
                    title: '24/7 Access',
                    desc: 'Buy ZESA tokens and manage your electricity anytime — day or night, weekday or holiday. No queues, no office hours.',
                    icon: 'fas fa-clock',
                },
                {
                    title: 'Instant Delivery',
                    desc: 'Your token is delivered within seconds via SMS and WhatsApp. No waiting, no delays — power up immediately.',
                    icon: 'fas fa-paper-plane',
                },
                {
                    title: 'Token History',
                    desc: 'Never lose a ZESA token again. View your complete purchase history anytime by entering your meter number.',
                    icon: 'fas fa-history',
                },
            ],
            steps: [
                { title: 'Enter Meter Number', desc: 'Type in your ZESA prepaid meter number on our secure platform.' },
                { title: 'Choose Amount & Pay', desc: 'Select your amount and pay instantly via EcoCash, Visa, or Mastercard.' },
                { title: 'Receive Your Token', desc: 'Get your ZESA token delivered via SMS and WhatsApp within seconds.' },
            ],
            faqs: [
                {
                    q: 'What is the ZESA self-service portal?',
                    a: 'The ZESA self-service portal on Magetsi is an online platform where you can buy ZESA prepaid tokens, view your token purchase history, check current electricity tariffs, and calculate how many units your budget will buy — all without visiting a ZETDC office.',
                },
                {
                    q: 'How do I buy ZESA tokens online?',
                    a: 'Visit <a href="/">magetsi.co.zw</a>, enter your prepaid meter number, choose your amount, and pay using EcoCash, Visa, or Mastercard. Your token is delivered instantly via SMS and WhatsApp.',
                },
                {
                    q: 'How do I check my ZESA token history?',
                    a: 'Go to the <a href="/zesa/view-token">View Tokens</a> page, enter your meter number, and see your most recent ZESA token purchases made through Magetsi — including the token number, amount, units, and date.',
                },
                {
                    q: 'Is Magetsi the official ZESA portal?',
                    a: 'Magetsi is not the official ZETDC website but is an authorised platform for purchasing ZESA prepaid tokens. We provide a faster, more convenient self-service experience with instant delivery, token history, and 24/7 availability.',
                },
                {
                    q: 'What payment methods are accepted?',
                    a: 'Magetsi accepts EcoCash (ZWG), Visa, and Mastercard (USD) for ZESA token purchases. You can also buy via our WhatsApp chatbot.',
                },
                {
                    q: 'Can I buy ZESA from outside Zimbabwe?',
                    a: 'Yes! With Visa or Mastercard payments, you can buy ZESA tokens for any meter in Zimbabwe from anywhere in the world. Many diaspora Zimbabweans use Magetsi to keep the lights on for their families back home.',
                },
                {
                    q: 'How do I check current ZESA tariffs?',
                    a: 'Visit the <a href="/zesa-tariffs">ZESA Tariffs</a> page on Magetsi to see the latest electricity tariff bands. You can also use our <a href="/calculators/zesa-calculator">ZESA Calculator</a> to see exactly how many units your money will buy.',
                },
            ],
        };
    },
    methods: {
        toggleFaq(index) {
            this.openFaq = this.openFaq === index ? null : index;
        },
    },
};
</script>

<style scoped>
/* Hero */
.portal-hero {
    position: relative;
    background: linear-gradient(135deg, #0f1b3d 0%, #1a56db 100%);
    padding: 60px 0 50px;
    overflow: hidden;
}
.hero-overlay {
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.hero-inner {
    position: relative;
    z-index: 1;
}
.hero-logo {
    filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
}
.hero-title {
    color: #fff;
    font-weight: 800;
    font-size: 2rem;
    margin-bottom: 12px;
}
.hero-subtitle {
    color: rgba(255,255,255,0.85);
    font-size: 1.05rem;
    max-width: 560px;
    margin: 0 auto 28px;
    line-height: 1.6;
}
.hero-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}
.hero-btn {
    font-weight: 600;
    font-size: 0.95rem;
    padding: 10px 24px;
    border-radius: 8px;
}

/* Section titles */
.section-title {
    font-weight: 700;
    font-size: 1.5rem;
    color: #1a1a2e;
}

/* Quick Actions */
.action-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 14px;
    padding: 20px 14px;
    text-align: center;
    transition: all 0.25s ease;
    cursor: pointer;
    color: inherit;
}
.action-card:hover {
    border-color: #c5d1f0;
    box-shadow: 0 8px 24px rgba(26,86,219,0.1);
    transform: translateY(-3px);
}
.action-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
}
.action-icon {
    font-size: 1.2rem;
    color: #fff;
}
.action-title {
    font-weight: 700;
    font-size: 0.9rem;
    color: #1a1a2e;
    margin-bottom: 4px;
}
.action-desc {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 0;
    line-height: 1.4;
}

/* Background sections */
.bg-light-section {
    background: #f7f8fc;
}

/* Features */
.feature-card {
    padding: 24px 16px;
}
.feature-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
    display: flex;
    align-items: center;
    justify-content: center;
}
.feature-icon {
    font-size: 1.3rem;
    color: #28a745;
}
.feature-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #1a1a2e;
    margin-bottom: 8px;
}
.feature-desc {
    font-size: 0.88rem;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 0;
}

/* Steps */
.steps-row {
    display: flex;
    justify-content: center;
    gap: 0;
    flex-wrap: wrap;
}
.step-item {
    text-align: center;
    flex: 1;
    min-width: 200px;
    max-width: 280px;
    padding: 0 16px;
    position: relative;
}
.step-number {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1a56db, #4a7cfb);
    color: #fff;
    font-weight: 800;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
}
.step-connector {
    display: none;
}
.step-title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1a1a2e;
    margin-bottom: 6px;
}
.step-desc {
    font-size: 0.85rem;
    color: #6b7280;
    line-height: 1.5;
}

/* SEO content */
.seo-content {
    max-width: 800px;
    font-size: 1rem;
    line-height: 1.75;
    color: #374151;
}
.seo-content h2 {
    font-size: 1.35rem;
}
.seo-content ul {
    padding-left: 20px;
}
.seo-content ul li {
    margin-bottom: 8px;
}
.seo-content a {
    color: #1a56db;
    text-decoration: underline;
}

/* FAQ */
.faq-section {
    max-width: 720px;
}
.faq-item {
    border: 1px solid #e9ecef;
    border-radius: 10px;
    margin-bottom: 10px;
    overflow: hidden;
    background: #fff;
}
.faq-question {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    cursor: pointer;
    transition: background 0.2s;
}
.faq-question:hover {
    background: #f9fafb;
}
.faq-q-text {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
    flex: 1;
    padding-right: 12px;
}
.faq-chevron {
    font-size: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
}
.faq-answer {
    padding: 0 20px 16px;
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.7;
}
.faq-answer a {
    color: #1a56db;
    text-decoration: underline;
}
.faq-slide-enter-active, .faq-slide-leave-active {
    transition: all 0.25s ease;
    max-height: 300px;
}
.faq-slide-enter, .faq-slide-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
}

/* Final CTA */
.portal-cta {
    background: linear-gradient(135deg, #0f1b3d 0%, #1a3a7a 100%);
    padding: 50px 0;
}
.cta-title {
    color: #fff;
    font-weight: 800;
    font-size: 1.6rem;
    margin-bottom: 10px;
}
.cta-subtitle {
    color: rgba(255,255,255,0.8);
    font-size: 1rem;
    margin-bottom: 24px;
}
.cta-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}
.btn-whatsapp {
    background: #25d366;
    border-color: #25d366;
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
}
.btn-whatsapp:hover {
    background: #1fb855;
    border-color: #1fb855;
    color: #fff;
}

/* Mobile-first responsive */
@media (max-width: 767px) {
    .portal-hero {
        padding: 40px 0 36px;
    }
    .hero-title {
        font-size: 1.55rem;
    }
    .hero-subtitle {
        font-size: 0.92rem;
    }
    .hero-btn {
        font-size: 0.88rem;
        padding: 10px 20px;
        width: 100%;
        max-width: 260px;
    }
    .section-title {
        font-size: 1.3rem;
    }
    .action-card {
        padding: 16px 10px;
    }
    .action-icon-wrap {
        width: 42px;
        height: 42px;
        border-radius: 12px;
    }
    .action-icon {
        font-size: 1.05rem;
    }
    .action-title {
        font-size: 0.82rem;
    }
    .action-desc {
        font-size: 0.7rem;
    }
    .step-item {
        min-width: 100%;
        margin-bottom: 20px;
    }
    .steps-row {
        flex-direction: column;
        align-items: center;
    }
}

@media (min-width: 768px) {
    .hero-title {
        font-size: 2.4rem;
    }
    .step-connector {
        display: block;
        position: absolute;
        top: 22px;
        right: -10%;
        width: 20%;
        height: 2px;
        background: linear-gradient(90deg, #4a7cfb, #c5d1f0);
    }
}
</style>
